import React from "react";
import { useEffect } from "react";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <section className="bg-gray-900 text-white py-16 px-6">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-4xl font-bold mb-6 text-purple-400">Trvllr™: Privacy Policy</h1>
        <p className="text-gray-400 mb-6">
          Last Updated: <span className="font-semibold text-white">[03.11.2025]</span>
        </p>

        {/* Introduction */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-purple-300 mb-3">1. Introduction</h2>
          <p className="text-gray-300">
            Welcome to Trvllr™. Your privacy is important to us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our platform.
          </p>
        </div>

        {/* Data Collection */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-purple-300 mb-3">2. Information We Collect</h2>
          <ul className="list-disc list-inside text-gray-300 space-y-2">
            <li>**Personal Information**: Name, email, phone number, profession, agency affiliation, etc.</li>
            <li>**Employment & Credential Data**: Licenses, certifications, work history, recruiter interactions.</li>
            <li>**Usage Data**: How you interact with Trvllr™, device information, and analytics.</li>
            <li>**Location Data**: Used for features like Shift Manager and TrIPS navigation assistance.</li>
            <li>**Financial Information**: If you use Trvllr Aid™ or ISO Auction™, necessary financial details are securely processed.</li>
          </ul>
        </div>

        {/* Data Usage */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-purple-300 mb-3">3. How We Use Your Information</h2>
          <ul className="list-disc list-inside text-gray-300 space-y-2">
            <li>To facilitate job matching and contract negotiations via **AutoJM™ & AutoNeg™**.</li>
            <li>To process applications and employment-related services (ISO Auction™, Trvllr Aid™, credential tracking).</li>
            <li>To improve user experience with personalized insights through **TrInsyte™**.</li>
            <li>To ensure compliance with industry regulations (e.g., HIPAA for sensitive healthcare data).</li>
            <li>To provide real-time navigation and schedule alerts through **Shift Manager™ and TrIPS™**.</li>
          </ul>
        </div>

        {/* Data Sharing */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-purple-300 mb-3">4. Data Sharing & Third Parties</h2>
          <p className="text-gray-300">
            Trvllr™ does not sell or rent user data. However, we may share limited data with:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2">
            <li>Recruiters and employers for job matching and contract facilitation.</li>
            <li>Third-party payment processors (for Trvllr Aid™ and premium subscriptions).</li>
            <li>Navigation services (Google Maps, Waze, Lyft for Shift Manager™ and TrIPS™).</li>
            <li>Compliance partners for credential verification and background checks.</li>
          </ul>
        </div>

        {/* Data Protection */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-purple-300 mb-3">5. How We Protect Your Data</h2>
          <p className="text-gray-300">
            We employ industry-standard security measures, including **encryption, two-factor authentication (2FA), and secure database storage** to safeguard your data.
          </p>
        </div>

        {/* User Rights */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-purple-300 mb-3">6. Your Rights & Choices</h2>
          <ul className="list-disc list-inside text-gray-300 space-y-2">
            <li>You can **update, correct, or delete** your data at any time in your profile settings.</li>
            <li>You can **opt-out of marketing communications** and email updates.</li>
            <li>California residents (CCPA) and EU users (GDPR) have additional data rights, including data portability.</li>
          </ul>
        </div>

        {/* Cookies & Tracking */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-purple-300 mb-3">7. Cookies & Tracking</h2>
          <p className="text-gray-300">
            We use cookies and analytics tools (Google Analytics, Facebook Pixel) to improve user experience and measure engagement. You can manage cookie preferences in your browser settings.
          </p>
        </div>

        {/* Policy Updates */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-purple-300 mb-3">8. Changes to This Policy</h2>
          <p className="text-gray-300">
            Trvllr™ may update this Privacy Policy as necessary. Users will be notified of significant changes via email or platform notifications.
          </p>
        </div>

        {/* Contact */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-purple-300 mb-3">9. Contact Us</h2>
          <p className="text-gray-300">
            If you have any questions or concerns about this Privacy Policy, you can contact us at:
          </p>
          <ul className="text-gray-400 mt-4 space-y-2">
            <li>📧 <a href="mailto:privacy@trvllrpro.io" className="text-purple-400 hover:text-purple-300">privacy@trvllrpro.io</a></li>
            <li>📍 Trvllr HQ: [4955 Sand Clouds Dr, Katy, TX, USA]</li>
          </ul>
        </div>
      </div>
    </section>
  );
};

export default PrivacyPolicy;
